import { Button, LoadingSpinner, Modal, SearchBar } from "@ai-teach/ui-web";
import { useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { HiFolder } from "react-icons/hi";
import { MdChevronRight, MdDriveFileMove } from "react-icons/md";

import { useSelection } from "@/contexts/SelectionContext";
import { useProjectNavigation } from "@/lib/hooks/modal/useProjectNavigation";
import { useMoveItems } from "@/lib/hooks/shared/useMoveItems";
import { useInView } from "react-intersection-observer";

interface MoveModalProps {
  isOpen: boolean;
  defaultNavigationStack?: Array<{ id: string; name: string }> | undefined;
}

export function MoveModal({ isOpen, defaultNavigationStack }: MoveModalProps) {
  const params = useParams({ strict: false });
  const currentRouteProjectId = params.projectId || null;

  const [searchText, setSearchText] = useState("");

  const {
    currentProjectId,
    projects,
    projectsLoading,
    hasMoreProjects,
    currentParent,
    navigationStack,
    handleProjectClick,
    navigateToIndex,
    resetNavigation,
    loadMoreProjects,
  } = useProjectNavigation(searchText, defaultNavigationStack);

  const { selectedLessonIds, selectedProjectIds, toggleMoveModal, clearAllSelections } =
    useSelection();
  const selectedItemsCount = selectedLessonIds.length + selectedProjectIds.length;

  const { moveItems, loading: moveItemsLoading } = useMoveItems();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleMove = async () => {
    const targetProjectId = currentProjectId;
    await moveItems(targetProjectId, currentRouteProjectId);
    handleClose();
  };

  const handleClose = () => {
    resetNavigation();
    setSearchText("");
    clearAllSelections();
    toggleMoveModal(false);
  };

  const { ref: projectsLoadMoreRef, inView: projectsInView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  useEffect(() => {
    if (projectsInView && hasMoreProjects && !projectsLoading) {
      loadMoreProjects();
    }
  }, [projectsInView, hasMoreProjects, projectsLoading, loadMoreProjects]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Move ${selectedItemsCount} item${selectedItemsCount !== 1 ? "s" : ""}`}
    >
      <div className="space-y-4">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search projects, lessons..."
        />

        {/* Breadcrumb navigation */}
        <nav
          aria-label="Folder breadcrumb"
          className="flex items-center gap-1 text-sm min-w-0 overflow-hidden flex-wrap"
        >
          <button
            type="button"
            onClick={() => navigateToIndex(-1)}
            className={`flex-shrink-0 font-medium transition-colors ${
              navigationStack.length === 0
                ? "text-secondary-200 cursor-default"
                : "text-gray-400 hover:text-primary-500"
            }`}
          >
            Home
          </button>
          {navigationStack.map((item, i) => {
            const isLast = i === navigationStack.length - 1;
            return (
              <span key={item.id} className="flex items-center gap-1 min-w-0">
                <MdChevronRight className="w-4 h-4 flex-shrink-0 text-gray-600" />
                <button
                  type="button"
                  onClick={() => navigateToIndex(i)}
                  className={`truncate font-medium transition-colors ${
                    isLast
                      ? "text-secondary-200 cursor-default"
                      : "text-gray-400 hover:text-primary-500"
                  }`}
                >
                  {item.name}
                </button>
              </span>
            );
          })}
        </nav>

        {/* Destination summary banner */}
        <div className="flex items-center gap-2 px-3 py-2 bg-primary rounded-md border border-gray-800">
          <MdDriveFileMove className="w-4 h-4 flex-shrink-0 text-white" />
          <p className="text-sm text-gray-300">
            Moving to:{" "}
            <span className="font-semibold text-white">{currentParent?.name ?? "Home"}</span>
          </p>
        </div>

        <div
          ref={scrollContainerRef}
          className="h-64 overflow-y-auto border border-gray-800 rounded-lg gradient-scrollbar"
        >
          <>
            {/* Root Level Option */}
            {!currentParent && (
              <button
                type="button"
                onClick={() => handleProjectClick(null)}
                className={`w-full flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-800 border-b border-gray-200 dark:border-gray-800 transition-colors rounded
                    ${currentProjectId === null ? "bg-primary-500/10 ring-2 ring-primary-500" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <HiFolder className="w-5 h-5 text-secondary-200" />
                  <span className="font-medium text-secondary-200">Root / Home</span>
                </div>
                {currentProjectId === null && (
                  <span className="text-xs font-semibold text-primary-500">Selected</span>
                )}
              </button>
            )}

            {projectsLoading && projects.length === 0 ? (
              <div className="p-4 flex gap-2 flex-col items-center justify-center h-full caption">
                <LoadingSpinner />
                <p className="animate-pulse">Loading...</p>
              </div>
            ) : projects.length === 0 && currentParent ? (
              <div className="flex flex-col items-center justify-center gap-2 h-full py-8 text-center">
                <MdDriveFileMove className="w-8 h-8 text-primary-500" />
                <p className="text-sm text-gray-400">This folder is empty</p>
                <p className="text-xs text-gray-600">Click Move to move items here</p>
              </div>
            ) : (
              <>
                {projects.map((project) => {
                  const isDisabled =
                    project.isLinkedCopy || project.hasLinkedCopy || moveItemsLoading;
                  const isSelected = currentProjectId === project.id;
                  return (
                    <button
                      key={project.id}
                      type="button"
                      onClick={() =>
                        !isDisabled && handleProjectClick({ id: project.id, name: project.name })
                      }
                      disabled={isDisabled}
                      className={`w-full flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-800 last:border-b-0 transition-colors rounded
                        ${isSelected ? "bg-primary-500/10 ring-2 ring-primary-500" : ""}
                        ${
                          isDisabled
                            ? "bg-gray-800/20 cursor-not-allowed opacity-60"
                            : "hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                        }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <HiFolder
                          className={`w-5 h-5 flex-shrink-0 ${isDisabled ? "text-gray-500" : "text-primary-500"}`}
                        />
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className={`font-medium truncate ${isDisabled ? "text-gray-500" : "text-secondary-200"}`}
                          >
                            {project.name}
                          </span>
                          {project.isLinkedCopy && (
                            <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">
                              Linked Copy
                            </span>
                          )}
                          {project.hasLinkedCopy && (
                            <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-500/20 text-red-400 rounded-full">
                              Has linked copy
                            </span>
                          )}
                          {project.id === currentRouteProjectId && (
                            <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-pink-500/20 text-pink-800 dark:bg-pink-50 rounded-full">
                              Current Folder
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isSelected && (
                          <span className="text-xs font-semibold text-primary-500">Selected</span>
                        )}
                        {!isDisabled && <MdChevronRight className="w-5 h-5 text-secondary-200" />}
                      </div>
                    </button>
                  );
                })}
                {/* Projects load more indicator */}
                {projects.length > 0 && hasMoreProjects && (
                  <div ref={projectsLoadMoreRef} className="flex justify-center py-8">
                    {projectsLoading && (
                      <div className="flex items-center gap-2 text-muted">
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span>Loading more projects...</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-800">
          <Button variant="secondary" size="sm" onClick={handleClose} disabled={moveItemsLoading}>
            Cancel
          </Button>

          <div className="flex items-center gap-3">
            {currentProjectId === currentRouteProjectId && !moveItemsLoading && (
              <span className="text-xs text-gray-500">Already here</span>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={handleMove}
              disabled={moveItemsLoading || currentProjectId === currentRouteProjectId}
            >
              {moveItemsLoading ? (
                <span className="flex items-center gap-2">
                  <LoadingSpinner />
                  Moving...
                </span>
              ) : (
                "Move"
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
